import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  isZeroQuotePlaceholder,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  BidStatus,
  contractByListingIdQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  interpUri,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'

import buyUrlPrefix from '@/buyer-portal/buyUrlPrefix'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import useCompatibleRouter from '@/buyer-portal/helpers/useCompatibleRouter'
import useBuyNowMutation from '@/buyer-portal/mutations/buyNowMutation'
import buyerOrderByListingIdQuery from '@/buyer-portal/queries/buyerOrderByListingIdQuery'
import { FeatureFlags } from '@/helpers/featureFlags'
import { sleep } from '@/helpers/sleep'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { sendTypedGTMEvent } from '@/helpers/telemetry/sendTypedGTMEvent'
import type { GTMAddToCartEvent } from '@/helpers/types/gtmTypes'

import { getContractAutoPayId, isContractListing } from '../../utils'
import { useSaveAutoPayMethod } from '../autoPayUtils'
import { useOnBuyNowError } from './useOnBuyNowError'

const ns = 'BuyNow.BuyNowActions'

export const useOnBuyNowConfirm = ({
  closeModal,
  listing,
  autoPayMethodId,
  isWireSelected = false,
  selectedQuote,
}: {
  closeModal: () => void
  listing: OrchestratedAuctionListingDetails
  autoPayMethodId: string | null
  isWireSelected?: boolean
  selectedQuote?: OrchestratedQuote | null
}) => {
  const { auction } = listing
  const auctionId = auction._id
  if (!auctionId) throw new Error('No auction id found')

  const accountId = useAccountId()
  const accessToken = useAccessToken()
  const router = useCompatibleRouter()
  const buyNowMutation = useBuyNowMutation()
  const onBuyNowError = useOnBuyNowError(auction)
  // Only a group with an early price makes this a First Dibs buy-now.
  const { buyerGroupId: fdBuyerGroupId, earlyPrice: fdEarlyPrice } =
    useFirstDibsPurchase(listing)
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)
  const contractAutoPayEnabled = useFlag(FeatureFlags.ENABLE_CONTRACT_AUTOPAY)
  const { saveAutoPayMethod } = useSaveAutoPayMethod()
  const isContract = isContractListing(listing)
  const contractAutoPayId = getContractAutoPayId(listing)
  const isShippingQuoteAvailable =
    !!selectedQuote && !isZeroQuotePlaceholder(selectedQuote)
  const autoPayEnabledForListing =
    autoPayEnforced &&
    (!isContract || (contractAutoPayEnabled && !!contractAutoPayId))

  const { refetch: refetchOrder } = buyerOrderByListingIdQuery.useWithOpts(
    [listing._id, BidStatus.WON],
    { enabled: false }
  )

  const { refetch: refetchContract } = contractByListingIdQuery.useWithOpts(
    [listing._id, accountId, 'buyer'],
    { enabled: false }
  )

  async function onBuyNowConfirm() {
    try {
      closeModal()

      const response = await buyNowMutation.mutateAsync(
        fdBuyerGroupId && fdEarlyPrice !== undefined
          ? {
              auctionId,
              buyerGroupId: fdBuyerGroupId,
              amount: fdEarlyPrice,
            }
          : { auctionId }
      )

      // Report what was charged: the early price for a First Dibs buy-now.
      const buyNowPrice = fdEarlyPrice ?? (listing.auction.buyNow?.price || 0)
      const itemInput = orchestratedListingToGTMItemInput(listing)
      const addToCartEvent: GTMAddToCartEvent = {
        event: 'add_to_cart',
        platform: GTM_PLATFORM,
        purchase_type: 'direct_buy',
        ecommerce: {
          currency: 'USD',
          value: buyNowPrice,
          items: [{ ...itemInput, price: buyNowPrice, index: 0, quantity: 1 }],
        },
      }
      sendTypedGTMEvent(addToCartEvent)

      if (
        autoPayEnabledForListing &&
        isShippingQuoteAvailable &&
        !listing.sellerCollects &&
        (autoPayMethodId || isWireSelected) &&
        accessToken
      ) {
        await saveAutoPayMethod({
          accessToken,
          accountId,
          isWireSelected,
          listing,
          paymentMethodId: autoPayMethodId ?? undefined,
        })
      }

      if (!response.winning) {
        showSnackbar({
          content: <FormattedMessage id={`${ns}.errors.403.ended`} />,
          options: { type: 'error' },
        })
        return
      }

      let orderFound = false
      let contractFound = false

      // Order/contract creation regularly outlasts a short poll; back off up
      // to ~24s before falling through to the pending-success path below.
      const pollDelaysMs = [
        1000, 1000, 2000, 2000, 3000, 3000, 3000, 3000, 3000, 3000,
      ]

      for (const delayMs of pollDelaysMs) {
        await sleep(delayMs)

        if (isContract) {
          const result = await refetchContract()

          if (result.isSuccess && result.data) {
            router.push(
              `${buyUrlPrefix}${interpUri`/user/${accountId ?? ''}/contracts/details/${result.data.groupId}`}`
            )
            contractFound = true
            break
          }
        } else {
          const result = await refetchOrder()

          if (result.isSuccess && result.data) {
            router.push(
              autoPayMethodId
                ? `${buyUrlPrefix}${interpUri`/user/${accountId ?? ''}/orders/details/${result.data._id}`}`
                : `${buyUrlPrefix}${interpUri`/user/${accountId ?? ''}/orders/details/${result.data._id}/checkout`}`
            )
            orderFound = true
            break
          }
        }
      }

      if (orderFound || contractFound) {
        showSnackbar({
          content: (
            <FormattedMessage
              id={isContract ? `${ns}.successContract` : `${ns}.success`}
            />
          ),
          options: { type: 'success' },
        })
      } else {
        // winning === true means the purchase is committed server-side —
        // the order/contract just isn't queryable yet. Never surface an
        // error for a successful purchase; send the buyer where it will
        // appear instead.
        showSnackbar({
          content: (
            <FormattedMessage
              id={
                isContract
                  ? `${ns}.successPendingContract`
                  : `${ns}.successPending`
              }
            />
          ),
          options: { type: 'success' },
        })
        router.push(
          isContract
            ? `${buyUrlPrefix}${interpUri`/user/${accountId ?? ''}/contracts`}`
            : `${buyUrlPrefix}${interpUri`/user/${accountId ?? ''}/orders`}`
        )
      }
    } catch (e) {
      onBuyNowError(e)
    }
  }

  return onBuyNowConfirm
}
